import { Flex } from 'components';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/_form';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { editTaskType, getTaskTypes } from 'api/taskTypes';
import { ITaskType } from 'types/taskType';
import { AddTaskTypeForm } from './components/Form/AddTaskTypeForm/AddTaskTypeForm';
import { EditTaskTypeForm } from './components/Form/EditTaskTypeForm/EditTaskTypeForm';
import { toast } from 'react-toastify';
import { getUserFunctions } from 'api/userFunctions';
import { displayTranslatedField } from 'utilities/localizedString';
import { useAdminTaskTypeContext } from 'contexts/AdminTaskTypeContext';
import { TaskTypesTable } from './components/TaskTypesTable/TaskTypesTable';

export const TaskTypes = () => {
  const [isAddFormOpen, setAddIsFormOpen] = useState(false);
  const [taskTypes, setTaskTypes] = useState<ITaskType[]>([]);
  const [editingTaskTypeId, setEditingTaskTypeId] = useState<number>();
  const [parentTaskTypeId, setParentTaskTypeId] = useState<number>();

  const { setUserFunctionsList } = useAdminTaskTypeContext();

  const { t } = useTranslation();

  const handleGetTaskTypes = async () => {
    const taskTypesRes = await getTaskTypes();

    if ('error' in taskTypesRes) {
      return;
    }

    setTaskTypes(taskTypesRes.data);
  };

  const handleChangeTaskTypeOrder = async (
    taskType: ITaskType,
    type: 'increase' | 'decrease'
  ) => {
    const wantedOrder =
      type === 'increase' ? taskType.order + 1 : taskType.order - 1;

    const editTaskTypeRes = await editTaskType(taskType.id, {
      task_type: {
        order: wantedOrder
      }
    });

    if ('error' in editTaskTypeRes) {
      toast.error(editTaskTypeRes.error.message);
      return;
    }

    setTaskTypes((prevTaskTypes) =>
      prevTaskTypes.map((prevTaskType) =>
        prevTaskType.id === editTaskTypeRes.data.id
          ? editTaskTypeRes.data
          : prevTaskType
      )
    );

    const taskTypeWithWantedOrder = taskTypes.find(
      ({ parent_id, order }) =>
        parent_id === taskType.parent_id && order === wantedOrder
    );

    if (taskTypeWithWantedOrder) {
      const editTaskTypeWithReplacedOrderRes = await editTaskType(
        taskTypeWithWantedOrder.id,
        {
          task_type: {
            order: taskType.order
          }
        }
      );

      if ('error' in editTaskTypeWithReplacedOrderRes) {
        toast.error(editTaskTypeWithReplacedOrderRes.error.message);
        return;
      }

      setTaskTypes((prevTaskTypes) =>
        prevTaskTypes.map((prevTaskType) =>
          prevTaskType.id === editTaskTypeWithReplacedOrderRes.data.id
            ? editTaskTypeWithReplacedOrderRes.data
            : prevTaskType
        )
      );
    }
  };

  const handleGetUserFunctionsList = useCallback(async () => {
    const { data } = await getUserFunctions();

    setUserFunctionsList(
      data.map((functionItem) => ({
        value: functionItem.id,
        label: displayTranslatedField(functionItem, 'name')
      }))
    );
  }, [setUserFunctionsList]);

  useEffect(() => {
    handleGetTaskTypes();
    void handleGetUserFunctionsList();
  }, [handleGetUserFunctionsList]);

  return (
    <>
      <Flex justifyContent="flex-end" width="100%" style={{ gap: '10px' }}>
        <Button
          variant="eucalyptus"
          onClick={() => {
            setAddIsFormOpen(true);
          }}
        >
          <FontAwesomeIcon icon={faPlusCircle} /> {t('buttons.add')}
        </Button>
      </Flex>

      <TaskTypesTable
        taskTypes={taskTypes}
        onEdit={(taskType) => setEditingTaskTypeId(taskType.id)}
        onAddSubType={(parentId) => setParentTaskTypeId(parentId)}
        onChangeOrder={handleChangeTaskTypeOrder}
      />

      <AddTaskTypeForm
        parentTaskTypeId={parentTaskTypeId}
        isOpen={isAddFormOpen || !!parentTaskTypeId}
        taskTypes={taskTypes}
        onCancelClick={() => {
          setAddIsFormOpen(false);
          setParentTaskTypeId(undefined);
        }}
        onSuccess={(newTaskType) => {
          setTaskTypes((prevTaskTypes) => [...prevTaskTypes, newTaskType]);
          setAddIsFormOpen(false);
          setParentTaskTypeId(undefined);
        }}
      />

      {editingTaskTypeId && (
        <EditTaskTypeForm
          isOpen={!!editingTaskTypeId}
          editingTaskTypeId={editingTaskTypeId}
          onCancelClick={() => {
            setEditingTaskTypeId(undefined);
          }}
          onSubmitSuccess={() => {
            handleGetTaskTypes();
            setEditingTaskTypeId(undefined);
          }}
          onDeleteSuccess={() => {
            handleGetTaskTypes();
            setEditingTaskTypeId(undefined);
          }}
        />
      )}
    </>
  );
};
